/**
 * Todo Core Service
 * Handles basic CRUD operations for todos
 * Part of the modularized todo service architecture
 */

import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from '../service-registry';
import { typedSupabase } from '@/lib/typed-supabase';
import { CreateTodoRequest, Todo, UpdateTodoRequest } from '@/types/todo';
import { logger } from '@/lib/logger';

export interface TodoQueryFilters {
  status?: string;
  priority?: string;
  assignedToUserId?: string;
  createdByUserId?: string;
  parentOnly?: boolean;
  searchTerm?: string;
  includeCompleted?: boolean;
  categoryId?: string;
  clientId?: string;
  projectId?: string;
  dueAfter?: string;
  dueBefore?: string;
  sortBy?: "created_at" | "updated_at" | "title" | "priority" | "due_date";
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface ITodoCoreService extends IService {
  createTodo(todo: CreateTodoRequest): Promise<ServiceResponse<Todo>>;
  getTodo(id: string): Promise<ServiceResponse<Todo>>;
  getTodos(organizationId?: string, filters?: TodoQueryFilters): Promise<ServiceResponse<Todo[]>>;
  getSubtasksByParentId(parentId: string, organizationId?: string): Promise<ServiceResponse<Todo[]>>;
  updateTodo(id: string, updates: UpdateTodoRequest): Promise<ServiceResponse<Todo>>;
  deleteTodo(id: string): Promise<ServiceResponse<void>>;
  getTopLevelTodos(): Promise<ServiceResponse<Todo[]>>;
}

export class TodoCoreService implements ITodoCoreService {
  readonly name = 'TodoCoreService';
  private initialized = false;

  async initialize(): Promise<void> {
    this.initialized = true;
    logger.info('TodoCoreService initialized');
  }

  async dispose(): Promise<void> {
    this.initialized = false;
    logger.info('TodoCoreService disposed');
  }

  /**
   * @deprecated Browser writes to `todos` must go through the TanStack DB
   * collection path (`useTodos` / `getTodosCollection`). Retained for
   * server-side and integration callers.
   */
  async createTodo(todo: CreateTodoRequest): Promise<ServiceResponse<Todo>> {
    try {
      logger.debug('Creating todo', { title: todo.title, organizationId: todo.organization_id });

      // First, try to get the current user
      const { data: { user } } = await typedSupabase.auth.getUser();
      if (!user) {
        logger.warn('No authenticated user found for todo creation');
        return createErrorResponse('Unauthorized: No authenticated user');
      }

      const { data, error } = await typedSupabase
        .from('todos')
        .insert({
          ...todo,
          created_by: user.id,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        logger.error('❌ Failed to create todo in database', { 
          error, 
          errorMessage: error.message,
          errorCode: error.code,
          todo 
        });
        throw error;
      }

      logger.info('Todo created successfully', { id: data.id, title: data.title });
      return createSuccessResponse(data as Todo);

    } catch (error) {
      logger.error('Error in createTodo', { error: error instanceof Error ? error.message : error });
      const errorMessage = error instanceof Error 
        ? error.message 
        : (typeof error === 'object' && error !== null && 'message' in error)
          ? String((error as any).message)
          : 'Create todo failed';
          
      return createErrorResponse(errorMessage);
    }
  }

  async getTodo(id: string): Promise<ServiceResponse<Todo>> {
    try {
      logger.debug('Getting todo', { id });

      const { data, error } = await typedSupabase
        .from('todos')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          logger.warn('Todo not found', { id });
          return createErrorResponse('Todo not found');
        }
        logger.error('Failed to get todo', { error: error.message, id });
        throw error;
      }

      logger.debug('Todo retrieved successfully', { id, title: data.title });
      return createSuccessResponse(data as Todo);

    } catch (error) {
      logger.error('Error in getTodo', { error: error instanceof Error ? error.message : error, id });
      return createErrorResponse(error instanceof Error ? error.message : 'Get todo failed');
    }
  }

  async getTodos(organizationId?: string, filters?: TodoQueryFilters): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Getting todos', { organizationId, filters });

      let query = typedSupabase
        .from('todos')
        .select('*');

      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      }

      if (filters) {
        if (filters.status) query = query.eq('status', filters.status);
        if (filters.priority) query = query.eq('priority', filters.priority);
        if (filters.assignedToUserId) query = query.eq('assigned_to', filters.assignedToUserId);
        if (filters.createdByUserId) query = query.eq('created_by', filters.createdByUserId);
        if (filters.parentOnly) query = query.is('parent_id', null);
        if (filters.includeCompleted === false && filters.status !== 'completed') {
          query = query.neq('status', 'completed');
        }
        if (filters.categoryId) query = query.eq('category_id', filters.categoryId);
        if (filters.clientId) query = query.eq('client_id', filters.clientId);
        if (filters.projectId) query = query.eq('project_id', filters.projectId);
        if (filters.dueAfter) query = query.gte('due_date', filters.dueAfter);
        if (filters.dueBefore) query = query.lte('due_date', filters.dueBefore);
        if (filters.searchTerm) {
          const term = filters.searchTerm.trim();
          query = query.or(`title.ilike.%${term}%,description.ilike.%${term}%`);
        }
      }

      const sortBy = filters?.sortBy ?? 'created_at';
      const ascending = filters?.sortOrder === 'asc';
      query = query.order(sortBy, { ascending, nullsFirst: false });

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }
      if (filters?.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit ?? 50) - 1);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Failed to get todos', { error: error.message, organizationId });
        throw error;
      }

      logger.debug('Todos retrieved successfully', { count: data.length, organizationId });
      return createSuccessResponse(data as Todo[]);

    } catch (error) {
      logger.error('Error in getTodos', { error: error instanceof Error ? error.message : error, organizationId });
      return createErrorResponse(error instanceof Error ? error.message : 'Get todos failed');
    }
  }

  /**
   * Fetch subtasks by parent_id. Optional organizationId scopes results for multi-tenant consistency.
   * Use this instead of getTodos()+filter so subtasks load reliably (e.g. when they have null organization_id).
   */
  async getSubtasksByParentId(parentId: string, organizationId?: string): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Getting subtasks by parent', { parentId, organizationId });

      let query = typedSupabase
        .from('todos')
        .select('*')
        .eq('parent_id', parentId)
        .order('created_at', { ascending: true });

      if (organizationId) {
        query = query.or(`organization_id.eq.${organizationId},organization_id.is.null`);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Failed to get subtasks', { error: error.message, parentId });
        throw error;
      }

      logger.debug('Subtasks retrieved successfully', { count: data?.length ?? 0, parentId });
      return createSuccessResponse((data ?? []) as Todo[]);
    } catch (error) {
      logger.error('Error in getSubtasksByParentId', {
        error: error instanceof Error ? error.message : error,
        parentId,
      });
      return createErrorResponse(
        error instanceof Error ? error.message : 'Get subtasks failed'
      );
    }
  }

  /**
   * @deprecated Browser writes to `todos` must go through the TanStack DB
   * collection path (`useTodos` / `getTodosCollection`). Retained for
   * server-side and integration callers.
   */
  async updateTodo(id: string, updates: UpdateTodoRequest): Promise<ServiceResponse<Todo>> {
    try {
      logger.debug('Updating todo', { id, updates: Object.keys(updates) });

      // Explicitly pick only columns that exist on the todos table to prevent
      // runtime fields (e.g. deprecated 'rizz' from joined profile data) from
      // reaching Supabase and causing a "column does not exist" error.
      const safeUpdates: UpdateTodoRequest = Object.fromEntries(
        Object.entries(updates).filter(([key]) =>
          (
            'title,description,status,priority,due_date,parent_id,category_id,' +
            'client_id,assigned_to,assigned_by,assigned_at,actual_end,' +
            'project_id,organization_id'
          ).split(',').includes(key)
        )
      ) as UpdateTodoRequest;

      const { data, error } = await typedSupabase
        .from('todos')
        .update({
          ...safeUpdates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          logger.warn('Todo not found for update', { id });
          return createErrorResponse('Todo not found');
        }
        logger.error('Failed to update todo', { error: error.message, id });
        throw error;
      }

      logger.info('Todo updated successfully', { id, title: data.title });
      return createSuccessResponse(data as Todo);

    } catch (error) {
      logger.error('Error in updateTodo', { error: error instanceof Error ? error.message : error, id });
      return createErrorResponse(error instanceof Error ? error.message : 'Update todo failed');
    }
  }

  /**
   * @deprecated Browser writes to `todos` must go through the TanStack DB
   * collection path (`useTodos` / `getTodosCollection`). Retained for
   * server-side and integration callers.
   */
  async deleteTodo(id: string): Promise<ServiceResponse<void>> {
    try {
      logger.debug('Deleting todo', { id });

      const { error } = await typedSupabase
        .from('todos')
        .delete()
        .eq('id', id);

      if (error) {
        logger.error('Failed to delete todo', { error: error.message, id });
        throw error;
      }

      logger.info('Todo deleted successfully', { id });
      return createSuccessResponse(undefined);

    } catch (error) {
      logger.error('Error in deleteTodo', { error: error instanceof Error ? error.message : error, id });
      return createErrorResponse(error instanceof Error ? error.message : 'Delete todo failed');
    }
  }

  async getTopLevelTodos(): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Getting top level todos');

      const { data, error } = await typedSupabase
        .from('todos')
        .select('*')
        .is('parent_id', null)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Failed to get top level todos', { error: error.message });
        throw error;
      }

      logger.debug('Top level todos retrieved successfully', { count: data.length });
      return createSuccessResponse(data as Todo[]);

    } catch (error) {
      logger.error('Error in getTopLevelTodos', { error: error instanceof Error ? error.message : error });
      return createErrorResponse(error instanceof Error ? error.message : 'Get top level todos failed');
    }
  }
}

// Export singleton instance
export const todoCoreService = new TodoCoreService();
